import React, { useState } from 'react';
import { Card, CardHeader, Box, Divider, Grid, Button } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/Close';
import AddCircleOutlineOutlinedIcon from '@material-ui/icons/AddCircleOutlineOutlined';
import {
  FilterChip,
  QuickSearch,
  useAPI,
  VerticalBoxContainer,
  PageContentWrapper,
} from '@shared/components';
import ExcelExportButton from '../../../../components/ExcelExportButton';
import useGlobalAlertContext from '../../../../hooks/useGlobalAlertContext';
import axios from '../../../../utils/axios';
import getChipValue from '../../../../utils/getChipValue';
import { handleChipDelete } from '../../../../utils/filterHelpers';
import { handleSortChange, getSortDirection } from '../../../../utils/sortHelpers';
import Modal from './Modal';
import List from './Table';
import Filter from './Filter';

const tableColumns = [
  {
    name: 'Korisnik kreirao',
    value: 'createdBy',
    align: 'left',
    width: 150,
    required: true,
    placeholder: 'korisniku',
  },
  {
    name: 'Komentar',
    value: 'documentComment',
    align: 'left',
    required: true,
    width: 300,
    placeholder: 'komentaru',
  },
  {
    name: 'Datum kreiranja',
    value: 'dateCreated',
    align: 'left',
    required: true,
    width: 100,
    placeholder: 'datumu kreiranja',
  },
  {
    name: '',
    value: '',
    align: 'left',
    width: 100,
  },
];

const exportFields = [
  {
    label: 'Korisnik kreirao',
    value: 'createdBy',
  },
  {
    label: 'Komentar',
    value: 'documentComment',
  },
  {
    label: 'Datum kreiranja',
    value: 'dateCreated',
    type: 'dateTime',
  },
];

const getLabelText = (value) => {
  const map = {
    createdBy: 'Kreirao',
    documentComment: 'Komentar',
    dateCreated: 'Datum kreiranja'
  };
  return map[value];
};

const Comments = ({ parentDataId }) => {
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({});
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sorters, setSorters] = useState([]);
  const [selectedComment, setSelectedComment] = useState(undefined);
  const [modalVisible, setModalVisible] = useState(false);
  const { sendSuccessAlertData, sendErrorAlertData } = useGlobalAlertContext();
  const [triggerRerender, setRerender] = useState(false);

  const [comments = { nodes: [], totalCount: 0 }, error, loading] = useAPI(
    () =>
      axios
        .get(`comments`, {
          params: {
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            conditions: { ...filters, documentId: parentDataId },
            orderBy: [...sorters, '"id" desc'],
          },
        })
        .then(({ data }) => data),
    [page, rowsPerPage, filters, sorters, triggerRerender],
  );

  return (
    <VerticalBoxContainer>
      <Filter
        currentValues={filters}
        visible={filterModalVisible}
        setVisible={setFilterModalVisible}
        setFilters={setFilters}
        setPage={setPage}
        getLabelText={getLabelText}
      />
      <Modal
        visible={modalVisible}
        setVisible={setModalVisible}
        selectedComment={selectedComment}
        setSelectedComment={setSelectedComment}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
        resetSelectedDocument={() => {
          setSelectedComment(undefined);
        }}
        setRerender={setRerender}
        parentDataId={parentDataId}
      />
      <PageContentWrapper style={{ paddingTop: '4px', paddingRight: '2px', paddingLeft: '2px' }}>
        <Card>
          <CardHeader
            title={
              <QuickSearch
                name="documentComment"
                placeholder="Brza pretraga po komentaru"
                setFilters={setFilters}
                mainFilter={filters}
                setPage={setPage}
              />
            }
            action={
              <Grid container spacing={4}>
                <Grid item>
                  <Button
                    color="primary"
                    disabled={Object.keys(filters).filter((k) => filters[k])?.length === 0}
                    startIcon={<CloseIcon fontSize="small" />}
                    onClick={() => setFilters({})}
                  >
                    <Box fontSize="small">Ukloni sve filtere</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon fontSize="small" />}
                    onClick={() => setFilterModalVisible(true)}
                  >
                    <Box fontSize="small">Filteri</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <ExcelExportButton
                    totalCount={comments.totalCount}
                    sortById={false}
                    variant="outlined"
                    fields={exportFields}
                    sorters={[...sorters, '"id" desc']}
                    filters={{...filters, documentId: parentDataId }}
                    routeName="comments"
                    fileName={`${parentDataId}-komentari`}
                  />
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={() => setModalVisible(true)}
                  >
                    <Box fontSize="small">Novi komentar</Box>
                  </Button>
                </Grid>
              </Grid>
            }
          />
          <Divider />
          {Object.keys(filters).filter((k) => filters[k])?.length !== 0 && (
            <>
              <Box ml={2} p={2} display="flex" alignItems="center" flexWrap="wrap">
                {Object.keys(filters).map(
                  (chip) =>
                    filters[chip] !== undefined && (
                      <FilterChip
                        color="primary"
                        variant="outlined"
                        key={chip}
                        label={`${getLabelText(chip)}: ${getChipValue(chip, filters)}`}
                        onDelete={() => handleChipDelete(chip, setFilters, setPage)}
                      />
                    ),
                )}
              </Box>
            </>
          )}

          <List
            loading={loading}
            error={error}
            comments={comments.nodes}
            tableColumns={tableColumns}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            totalCount={comments.totalCount}
            handleSortChange={handleSortChange}
            getSortDirection={getSortDirection}
            setSorters={setSorters}
            sorters={sorters}
            // setDeleteModalVisible={setDeleteModalVisible}
            // setSelectedComment={setSelectedComment}
            // setModalVisible={setModalVisible}
          />
        </Card>
      </PageContentWrapper>
    </VerticalBoxContainer>
  );
};

export default Comments;
